/**
 * Thanksgiving bookmarks — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thanksgivingBookmarksPack: ToilePack = {
    slug: 'thanksgiving-bookmarks',
    category: 'season',
    name: 'Thanksgiving bookmarks',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'turkey', label: 'Turkey', subject: 'A sheet of four tall bookmarks each with a turkey motif, a tassel hole at the top, dashed cut lines'},
        {key: 'leaves', label: 'Leaves', subject: 'A sheet of four tall bookmarks each with a column of falling leaves, a tassel hole, dashed cut lines'},
        {key: 'thanks', label: 'Thanks', subject: 'A sheet of four tall bookmarks each lettered "Give Thanks" down the length with wheat, a tassel hole, dashed cut lines'},
    ],
    styles: [
        {key: 'rustic', label: 'Rustic kraft', style: 'rustic printable on kraft paper, leaf and wheat borders, warm serif lettering, print-ready', palette: 'Kraft brown, rust, mustard and cream', framing: 'Laid out on a kraft page with an even margin'},
        {key: 'elegant', label: 'Elegant', style: 'elegant printable, thin gold line motifs, refined serif lettering, print-ready', palette: 'Cream, gold and a deep burgundy', framing: 'Laid out on a cream page with wide margins'},
        {key: 'kids', label: 'Kids', style: "kids' thanksgiving printable, cartoon turkeys and leaves, rounded lettering, print-ready", palette: 'Bright orange, yellow, red and brown', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['as a set of two', 'with a ribbon tassel', 'for a classroom gift'],
    directionHint: 'try: with a ribbon tassel',
    seo: {
        title: 'Thanksgiving bookmarks: turkey, leaves and thanks',
        metaDescription: "Nine Thanksgiving bookmarks — a turkey, a leaf column, a lettered THANKS — rustic, elegant or kids' style, cut lines included.",
        h1: 'Thanksgiving bookmarks: turkey, leaves and thanks in three styles',
        tagline: 'Nine sheets, three bookmark designs, three styles each — four tall bookmarks to a page.',
        intro: "A bookmark sheet you actually cut: four tall bookmarks to a page, ready with cut lines. Three designs carry the set — a turkey, a column of falling leaves, a lettered THANKS — each drawn rustic burlap-and-kraft, elegant gold-and-cream, or bright kids' style. Print on card stock for a bookmark that holds up to daily use. Type a different word and the set redraws.",
        useCases: [
            'a classroom reading reward or party favor',
            'a Thanksgiving-themed book fair bookmark',
            'a handmade gift tucked into a book',
            'a library display or reading corner craft',
        ],
        faq: [
            {q: 'What size are the bookmarks?', a: 'Four tall bookmarks are laid out per page with cut lines between them, sized to a standard bookmark.'},
            {q: 'What paper should I print on?', a: 'Card stock holds up best to handling; standard printer paper works fine for a one-time favor.'},
            {q: 'Can I change the lettering or motif?', a: "Yes — type a direction like 'say GRATEFUL instead' or 'in orange and cream' and the sheet redraws in the same three styles."},
            {q: 'Is there a version for a family gathering, not a classroom?', a: "The elegant set suits a formal table favor, while the kids' set is bright and playful and the rustic set sits in between."},
        ],
    },
}
